import { integrationApi, privateApi, temporaryApi } from 'api'
import { AxiosResponse } from 'axios'
import {
  ISeller,
  ISellersQueryRequest,
  ISellersQueryResponse,
  IAdminCreateSellerRequest,
  IActionsSellersRequest,
  IAdminUpdateSellerRequest,
  IShop,
  ICreateSellerShopRequest,
  IDeliveryWays,
  IAdminUpdateDeliveryWaysRequest,
  IOwnDelivery,
  IAdminUpdateOwnDeliveryRequest,
  INearestPointOfCdek,
  IUpdateNearestPointOfCdekRequest,
  IAdminGetChartDataRequest,
  IAdminGetChartDataResponse,
  IAdminChangeCommissionRequest,
  IAdminChangeCommissionResponse,
  IAdminChangeCommissionDiscountRequest,
  IAdminChangeCommissionDiscountResponse,
  IAdminSellerSettings,
  IAdminSellerSettingsRequest,
  IAdminSellerActivity,
  IAdminSellersSummary,
  IAdminChangeSellerStatusRequest,
  IGetInfoOfLegalRequest,
  IGetInfoOfLegalResponse,
  IAdminCreatePhysicalSellerRequest,
  IAdminCreateWithdrawalRequest,
  IIntegrationStatusRequest,
  IIntegrationStatusResponse,
  IImportMoySkladProductsRequest,
  IImportExcelHistoryRequest,
  IImportExcelHistoryResponse,
  ILastExcelStockUpdateResponse,
  IStockConfirmationStatus,
  IUpdateSellerHiddenRequest,
  TSellerDisableInfo,
  IOneCSettingsResponse,
  IInsalesSettingsResponse,
  IInsalesProbeResponse,
  IInsalesSyncResponse,
  IUpdateInsalesSettingsRequest,
  IProbeInsalesRequest,
  IUpdateSellerHolidaysRequest,
  ISellerStatusResponse,
  IUpdateSellerHolidaysResponse,
  IUpdateOneCCreateNewProductsRequest,
  ISaveSellerHiddenCategoriesRequest,
  ISellerHiddenCategoryRecord,
  IHiddenCategoriesPreviewResponse,
  IMoySkladGetLinkersRequest,
  IMoySkladGetLinkersResponse,
  IMoySkladRefreshGroupsRequest,
  IMoySkladSaveAndSyncRequest,
  IMoySkladSelectSaleFieldRequest,
  IMoySkladSaveLinkersRequest,
  IMoySkladSaveTokenRequest,
  IMoySkladSyncStatusRequest,
  IMoySkladSyncStatusResponse,
  MoySkladLinkerRequest,
  MoySkladLinkerResponse,
  MoySkladLinksRequest
} from 'models/seller'
import { IUpdateSellerLogisticsRequest } from 'models/yandexDelivery'
import { getNormalizedFormData } from 'shared/utils/getNormalizedFormData'

// Продавец работает за себя, админ — за выбранного продавца через seller_id
const insalesQuery = (sellerId?: string) =>
  sellerId ? `?seller_id=${encodeURIComponent(sellerId)}` : ''

export const sellerApi = {
  getStatus(): Promise<AxiosResponse<ISellerStatusResponse>> {
    return privateApi.get<ISellerStatusResponse>('seller/status')
  },
  // withProductsFlag — проставить каждому продавцу has_published_products
  getSearchSellers(
    withProductsFlag?: boolean
  ): Promise<AxiosResponse<ISeller[]>> {
    return privateApi.get<ISeller[]>(
      `admin/seller/search${withProductsFlag ? '?with_products_flag=true' : ''}`
    )
  },
  getSellers({
    skip,
    limit,
    all_fields,
    number,
    full_name,
    shop_name,
    tel,
    status,
    disable_state,
    status_first
  }: ISellersQueryRequest): Promise<AxiosResponse<ISellersQueryResponse>> {
    return privateApi.get<ISellersQueryResponse>(
      `admin/seller?skip=${skip}&limit=${limit}&all_fields=${all_fields ?? ''
      }&number=${number ?? ''}&full_name=${full_name ?? ''}&shop_name=${shop_name ?? ''
      }&tel=${tel ?? ''}&status=${status ?? ''}&disable_state=${disable_state ?? ''
      }&status_first=${status_first ?? ''}`
    )
  },
  getSellersSummary(): Promise<AxiosResponse<IAdminSellersSummary>> {
    return privateApi.get<IAdminSellersSummary>('admin/seller/summary')
  },
  getSeller(sellerId: string): Promise<AxiosResponse<ISeller>> {
    return privateApi.get<ISeller>(`admin/seller/${sellerId}`)
  },
  setActions({ toDelete }: IActionsSellersRequest): Promise<AxiosResponse> {
    return privateApi.put(`admin/seller?toDelete=${toDelete}`)
  },
  addSeller(data: IAdminCreateSellerRequest | IAdminCreatePhysicalSellerRequest): Promise<AxiosResponse> {
    const formData = getNormalizedFormData(data)
    return privateApi.post('admin/seller', formData)
  },
  updateSellerInfo(data: IAdminUpdateSellerRequest): Promise<AxiosResponse> {
    const formData = getNormalizedFormData(data)
    return privateApi.patch('admin/seller', formData)
  },
  updateSellerHidden(
    sellerId: string,
    data: IUpdateSellerHiddenRequest
  ): Promise<AxiosResponse<TSellerDisableInfo>> {
    return privateApi.patch<TSellerDisableInfo>(`admin/seller/${sellerId}/hidden`, data)
  },
  updateSellerHolidays(
    data: IUpdateSellerHolidaysRequest
  ): Promise<AxiosResponse<IUpdateSellerHolidaysResponse>> {
    const { seller_id, ...body } = data

    return privateApi.patch<IUpdateSellerHolidaysResponse>(
      `seller/holidays${seller_id ? `?seller_id=${encodeURIComponent(seller_id)}` : ''}`,
      body
    )
  },
  updateOneCCreateNewProducts({
    create_new_products,
    fill_description_on_create,
    seller_id
  }: IUpdateOneCCreateNewProductsRequest): Promise<AxiosResponse<IOneCSettingsResponse>> {
    const params = new URLSearchParams()
    if (seller_id) {
      params.append('seller_id', seller_id)
    }

    const query = params.toString()

    return privateApi.patch<IOneCSettingsResponse>(
      `1c/create-new-products${query ? `?${query}` : ''}`,
      { create_new_products, fill_description_on_create }
    )
  },
  getInsalesSettings(sellerId?: string): Promise<AxiosResponse<IInsalesSettingsResponse>> {
    return privateApi.get<IInsalesSettingsResponse>(
      `insales/settings${insalesQuery(sellerId)}`
    )
  },
  updateInsalesSettings(
    data: IUpdateInsalesSettingsRequest,
    sellerId?: string
  ): Promise<AxiosResponse<IInsalesSettingsResponse>> {
    return privateApi.patch<IInsalesSettingsResponse>(
      `insales/settings${insalesQuery(sellerId)}`,
      data
    )
  },
  probeInsales(
    data: IProbeInsalesRequest,
    sellerId?: string
  ): Promise<AxiosResponse<IInsalesProbeResponse>> {
    return privateApi.post<IInsalesProbeResponse>(
      `insales/probe${insalesQuery(sellerId)}`,
      data
    )
  },
  syncInsales(sellerId?: string): Promise<AxiosResponse<IInsalesSyncResponse>> {
    return privateApi.post<IInsalesSyncResponse>(
      `insales/sync${insalesQuery(sellerId)}`
    )
  },
  getShops(sellerId: string): Promise<AxiosResponse<IShop[]>> {
    return privateApi.get<IShop[]>(`admin/seller/shop/${sellerId}`)
  },
  deleteShop(shopId: string): Promise<AxiosResponse> {
    return privateApi.delete(`admin/seller/shop?id=${shopId}`)
  },
  createShop(data: ICreateSellerShopRequest): Promise<AxiosResponse> {
    return privateApi.post('admin/seller/shop', data)
  },
  getDeliveryWays(sellerId: string): Promise<AxiosResponse<IDeliveryWays>> {
    return privateApi.get<IDeliveryWays>(`admin/seller/delivery-ways/${sellerId}`)
  },
  changeDeliveryWays(data: IAdminUpdateDeliveryWaysRequest): Promise<AxiosResponse> {
    return privateApi.patch('admin/seller/delivery-ways', data)
  },
  getOwnDelivery(sellerId: string): Promise<AxiosResponse<IOwnDelivery>> {
    return privateApi.get<IOwnDelivery>(`admin/seller/own-delivery/${sellerId}`)
  },
  updateOwnDelivery(data: IAdminUpdateOwnDeliveryRequest): Promise<AxiosResponse<IOwnDelivery>> {
    return privateApi.patch<IOwnDelivery>('admin/seller/own-delivery', data)
  },
  getSelectedDeliveryPoint(sellerId: string): Promise<AxiosResponse<INearestPointOfCdek>> {
    return privateApi.get<INearestPointOfCdek>(`admin/seller/point-of-cdek/${sellerId}`)
  },
  updateSelectedDeliveryPoint(data: IUpdateNearestPointOfCdekRequest): Promise<AxiosResponse> {
    return privateApi.post('admin/seller/point-of-cdek', data)
  },
  updateLogistics(
    sellerId: string,
    data: IUpdateSellerLogisticsRequest
  ): Promise<AxiosResponse<ISeller>> {
    return privateApi.patch<ISeller>(`admin/seller/${sellerId}/logistics`, data)
  },
  getSalesVolume({ year, seller_id }: IAdminGetChartDataRequest): Promise<AxiosResponse<IAdminGetChartDataResponse>> {
    return privateApi.get<IAdminGetChartDataResponse>(`admin/seller/chart/data?year=${year}&seller_id=${seller_id}`)
  },
  changeCommission(sellerId: string, data: IAdminChangeCommissionRequest): Promise<AxiosResponse<IAdminChangeCommissionResponse>> {
    return privateApi.patch<IAdminChangeCommissionResponse>(`admin/seller/change-commission/${sellerId}`, data)
  },
  changeCommissionDiscount(
    sellerId: string,
    data: IAdminChangeCommissionDiscountRequest
  ): Promise<AxiosResponse<IAdminChangeCommissionDiscountResponse>> {
    return privateApi.patch<IAdminChangeCommissionDiscountResponse>(`admin/seller/commission-discount/${sellerId}`, data)
  },
  getSellerSettings(sellerId: string): Promise<AxiosResponse<IAdminSellerSettings>> {
    return privateApi.get<IAdminSellerSettings>(`admin/seller/settings/${sellerId}`)
  },
  updateSellerSettings(
    sellerId: string,
    data: IAdminSellerSettingsRequest
  ): Promise<AxiosResponse<IAdminSellerSettings>> {
    return privateApi.patch<IAdminSellerSettings>(`admin/seller/settings/${sellerId}`, data)
  },
  getSellerActivity(sellerId: string): Promise<AxiosResponse<IAdminSellerActivity>> {
    return privateApi.get<IAdminSellerActivity>(`admin/seller/activity/${sellerId}`)
  },
  changeSellerStatus(sellerId: string, data: IAdminChangeSellerStatusRequest): Promise<AxiosResponse> {
    return privateApi.patch(`admin/seller/change-status/${sellerId}`, data)
  },
  checkLegalINN(data: IGetInfoOfLegalRequest, token?: string): Promise<AxiosResponse<IGetInfoOfLegalResponse>> {
    if (token) {
      return temporaryApi.post<IGetInfoOfLegalResponse>(`dadata/legal?INN=${data.INN}`, undefined, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
    }

    return privateApi.post<IGetInfoOfLegalResponse>(`dadata/legal?INN=${data.INN}`)
  },
  checkSellerDataOnFilling(sellerId: string): Promise<AxiosResponse> {
    return privateApi.get(`admin/seller/check-for-filling/${sellerId}`)
  },
  withdrawalRequest(data: IAdminCreateWithdrawalRequest): Promise<AxiosResponse> {
    return privateApi.post('admin/seller/withdraw', data)
  },
  getIntegrationStatus({ seller_id, type }: IIntegrationStatusRequest): Promise<AxiosResponse<IIntegrationStatusResponse>> {
    return integrationApi.get<IIntegrationStatusResponse>(`requests?seller_id=${seller_id}&type=${type}`)
  },
  moySkladImportProducts(data: IImportMoySkladProductsRequest): Promise<AxiosResponse> {
    return integrationApi.post('moy-sklad/import', data)
  },
  moySkladGetCategories({ seller_id }: MoySkladLinkerRequest): Promise<AxiosResponse<MoySkladLinkerResponse>> {
    return integrationApi.get<MoySkladLinkerResponse>(`moy-sklad-linker?seller_id=${seller_id}`)
  },
  moySkladGetLinkers({
    seller_id,
    skip,
    limit
  }: IMoySkladGetLinkersRequest): Promise<AxiosResponse<IMoySkladGetLinkersResponse>> {
    return integrationApi.get<IMoySkladGetLinkersResponse>(
      `moy-sklad-linker?seller_id=${seller_id}&skip=${skip}&limit=${limit}`
    )
  },
  moySkladSaveToken(data: IMoySkladSaveTokenRequest): Promise<AxiosResponse> {
    return integrationApi.post('moy-sklad/save-token', data)
  },
  moySkladRefreshGroups(data: IMoySkladRefreshGroupsRequest): Promise<AxiosResponse> {
    return integrationApi.post('moy-sklad/refresh-groups', data)
  },
  moySkladSaveAndSync(data: IMoySkladSaveAndSyncRequest): Promise<AxiosResponse> {
    return integrationApi.post('moy-sklad/save-and-sync', data)
  },
  moySkladSelectSaleField(
    sellerId: string,
    data: IMoySkladSelectSaleFieldRequest
  ): Promise<AxiosResponse> {
    return privateApi.patch(`seller/${sellerId}/sale-field`, data)
  },
  moySkladGetSyncStatus({
    seller_id
  }: IMoySkladSyncStatusRequest): Promise<AxiosResponse<IMoySkladSyncStatusResponse>> {
    return integrationApi.get<IMoySkladSyncStatusResponse>(
      `moy-sklad/sync-status?seller_id=${seller_id}`
    )
  },
  connectMoySkladCategoriesV2(data: IMoySkladSaveLinkersRequest): Promise<AxiosResponse> {
    return integrationApi.post('moy-sklad-linker', data)
  },
  connectMoySkladCategories(data: MoySkladLinksRequest) {
    return integrationApi.post('moy-sklad-linker', data)
  },
  getExcelImportHistory({
    skip,
    limit,
    seller_id
  }: IImportExcelHistoryRequest): Promise<AxiosResponse<IImportExcelHistoryResponse>> {
    return privateApi.get<IImportExcelHistoryResponse>(
      `import/report/import-excel-files?skip=${skip}&limit=${limit}&seller_id=${seller_id ?? ''}`
    )
  },
  getLastExcelStockUpdate(
    sellerId?: string
  ): Promise<AxiosResponse<ILastExcelStockUpdateResponse>> {
    const params = new URLSearchParams()
    if (sellerId) {
      params.append('seller_id', sellerId)
    }

    const query = params.toString()

    return privateApi.get<ILastExcelStockUpdateResponse>(
      `import/report/last-excel-stock-update${query ? `?${query}` : ''}`
    )
  },
  getStockConfirmationStatus(
    sellerId?: string
  ): Promise<AxiosResponse<IStockConfirmationStatus>> {
    const params = new URLSearchParams()
    if (sellerId) {
      params.append('seller_id', sellerId)
    }

    const query = params.toString()
    const path = sellerId ? 'admin/seller/stock-confirmation' : 'seller/stock-confirmation'

    return privateApi.get<IStockConfirmationStatus>(
      `${path}${query ? `?${query}` : ''}`
    )
  },
  markCabinetActivity(): Promise<AxiosResponse<void>> {
    return privateApi.post<void>('seller/cabinet-activity')
  },
  confirmStockActuality(): Promise<AxiosResponse<IStockConfirmationStatus>> {
    return privateApi.post<IStockConfirmationStatus>('seller/stock-confirmation')
  },
  setMoySkladSyncEnabled(
    enabled: boolean,
    sellerId?: string
  ): Promise<AxiosResponse<IStockConfirmationStatus>> {
    const query = sellerId ? `?seller_id=${sellerId}` : ''

    return privateApi.patch<IStockConfirmationStatus>(
      `seller/moysklad-sync${query}`,
      { enabled }
    )
  },
  exportSellersProductsToExcel(sellerId?: string): Promise<AxiosResponse<Blob>> {
    return privateApi.get<Blob>(`export/report/exportSellersProductsToExcel?seller_id=${sellerId ?? ''}`, {
      responseType: 'blob'
    })
  },
  exportPatternProductsExcel(sellerId?: string): Promise<AxiosResponse<Blob>> {
    return privateApi.get<Blob>(`export/report/exportPatternProductsExcel?seller_id=${sellerId ?? ''}`, {
      responseType: 'blob'
    })
  },
  exportSellersReport(): Promise<AxiosResponse<Blob>> {
    return privateApi.get<Blob>('export/report/sellers', { responseType: 'blob' })
  },
  exportSellersActivityReport(dateFrom: string, dateTo: string): Promise<AxiosResponse<Blob>> {
    return privateApi.get<Blob>(`export/report/sellers-activity?dateFrom=${dateFrom}&dateTo=${dateTo}`, {
      responseType: 'blob'
    })
  },
  importSellersProducts(file: File, sellerId?: string): Promise<AxiosResponse> {
    const formData = new FormData()
    formData.append('file', file)
    if (sellerId) {
      formData.append('seller_id', sellerId)
    }
    return privateApi.post('import/report/import-sellers-products', formData)
  },
  getAdminSellerHiddenCategories(
    sellerId: string
  ): Promise<AxiosResponse<ISellerHiddenCategoryRecord[]>> {
    return privateApi.get<ISellerHiddenCategoryRecord[]>(
      `admin/seller/${sellerId}/hidden-categories`
    )
  },
  saveAdminSellerHiddenCategories(
    sellerId: string,
    data: ISaveSellerHiddenCategoriesRequest
  ): Promise<AxiosResponse<ISellerHiddenCategoryRecord[]>> {
    return privateApi.post<ISellerHiddenCategoryRecord[]>(
      `admin/seller/${sellerId}/hidden-categories`,
      data
    )
  },
  previewAdminSellerHiddenCategories(
    sellerId: string,
    data: ISaveSellerHiddenCategoriesRequest
  ): Promise<AxiosResponse<IHiddenCategoriesPreviewResponse>> {
    return privateApi.post<IHiddenCategoriesPreviewResponse>(
      `admin/seller/${sellerId}/hidden-categories/preview`,
      data
    )
  },
  getSellerHiddenCategories(): Promise<AxiosResponse<ISellerHiddenCategoryRecord[]>> {
    return privateApi.get<ISellerHiddenCategoryRecord[]>(
      'seller/hidden-categories'
    )
  },
  saveSellerHiddenCategories(
    data: ISaveSellerHiddenCategoriesRequest
  ): Promise<AxiosResponse<ISellerHiddenCategoryRecord[]>> {
    return privateApi.post<ISellerHiddenCategoryRecord[]>(
      'seller/hidden-categories',
      data
    )
  },
  previewSellerHiddenCategories(
    data: ISaveSellerHiddenCategoriesRequest
  ): Promise<AxiosResponse<IHiddenCategoriesPreviewResponse>> {
    return privateApi.post<IHiddenCategoriesPreviewResponse>(
      'seller/hidden-categories/preview',
      data
    )
  },
  recalculateBalance(sellerId: string): Promise<AxiosResponse<ISeller>> {
    return privateApi.post<ISeller>(`admin/seller/${sellerId}/recalculate-balance`)
  }
}
